import { ctaTranslate } from "../../lib/ctaTranslate";
import { ProductNameText } from "../../lib/ProductNameText";
import { productDisplayName } from "../../lib/productText";
import { Button } from "../../ui";
import { useSkuCopyToast } from "../../lib/skuCopyToast";
import {
  fallbackProductImage,
  handleProductImageError,
  normalizeProductImage,
} from "../../lib/fallback-image";
import {
  navigateExternal,
  gaProductCardClick,
  gaProductCardClickPayload,
} from "../../lib/postmessage";
import { recordSummaryViewMore } from "../../api/summaryViewMore";
import { RatingStars } from "../RatingStars";
import type { EnergyLabelInfo } from "../../types";
import {
  PickBadges,
  PickProductPrice,
  type PickBadgeEntry,
  type PickPriceBlock,
} from "./pickShared";
import { cartSkuForProduct } from "./cartSku";

/* ai_top_picks(guide U2) / Feature 기획 §4 공용 제품 카드(`pick-item`).
 * ai_top_picks 리스트와 concept_compare_table 대표제품이 같은 카드를 재사용한다
 * (coding-standards: 평행 구조 금지 — 단일 SoT). */

export interface PickProductItem {
  sku: string;
  feedSku?: string;
  rawSku?: string;
  modelCode?: string;
  name?: string;
  image?: string | null;
  category?: string;
  price?: PickPriceBlock | number;
  rating?: number | null;
  reviewCount?: number;
  keyFeatures?: string[];
  energyLabel?: string | EnergyLabelInfo;
  badges?: PickBadgeEntry[];
  isBundle?: boolean;
  shortDescription?: string;
  isAnchor?: boolean;
  pdpUrl?: string;
  /** purchasable | out_of_stock | not_for_sale. 서버 상업 상태. */
  availability?: "purchasable" | "out_of_stock" | "not_for_sale";
}

/* display 표기언어(languageCode) 라벨 — 서버 payload(data.labels)에서 주입.
 * 미지정(개념비교 등 미배선 경로)이면 아래 영어 상수로 폴백한다(i18n SOT). */
export interface PickCardLabels {
  learnMore?: string;
  addToBasket?: string;
  outOfStock?: string;
  notForSale?: string;
}

const _LEARN_MORE_LABEL = "Learn More";
const _ADD_TO_BASKET_LABEL = "Add to Basket";
// (4-4) Key features — 구어체 1줄(런타임 즉시 생성). 과거 2줄 명사형 배치 산출물 폐지.
const _MAX_FEATURES = 1;

export function PickProductCard({
  item,
  sessionToken,
  labels,
}: {
  item: PickProductItem;
  sessionToken: string;
  labels?: PickCardLabels;
}) {
  const { copySku } = useSkuCopyToast();
  const displayName = productDisplayName(item.name, item.sku);
  const keyFeatures = item.keyFeatures || [];
  const isHighlight = !!item.isAnchor;
  const badges = item.badges || [];
  const itemClass = isHighlight ? "pick-item highlight" : "pick-item";
  const learnMoreLabel = labels?.learnMore || _LEARN_MORE_LABEL;
  const addToBasketLabel = labels?.addToBasket || _ADD_TO_BASKET_LABEL;
  const canPurchase = item.availability == null || item.availability === "purchasable";
  const availabilityLabel =
    item.availability === "out_of_stock"
      ? labels?.outOfStock || "Out of stock"
      : item.availability === "not_for_sale"
        ? labels?.notForSale || "Not for sale"
        : "";

  const handleSkuCopy = () => {
    if (item.sku) {
      copySku(item.sku);
    }
  };

  const handleLearnMore = () => {
    if (item.sku && sessionToken) {
      recordSummaryViewMore(sessionToken, item.sku);
    }
    if (item.pdpUrl) {
      // B-5-2: PDP 진입은 페이지 이동과 1쌍 — asklg_shop_product_card_click 를 navigate 에 임베드.
      // (별도 ga.custom_event 미발신; BRFrame 이 GA→navigate 2건으로 relay)
      navigateExternal(
        item.pdpUrl,
        undefined,
        { sessionId: sessionToken },
        gaProductCardClickPayload(item.sku, item.isAnchor),
      );
      return;
    }
    // pdpUrl 없으면 챗 프롬프트(페이지 이동 없음) — 단독 product_card_click 발신.
    gaProductCardClick(item.sku, item.isAnchor);
    void ctaTranslate({
      code: "recommend.tell_me_more_about",
      params: { name: displayName },
      sessionToken,
      fallback: `Tell me more about ${displayName}`,
    });
  };

  const handleAddToBasket = () => {
    if (!canPurchase) {
      return;
    }
    const cartSku = cartSkuForProduct(item);
    // asklg_shop_add_to_cart_click 는 백엔드가 commerce_add_to_cart 성공 시 발화한다
    // (말로 담기 = 카드 버튼 동일 KPI, 성공 시에만). 위젯은 도구 호출만 한다.
    void ctaTranslate({
      code: "recommend.add_1x_to_my_cart",
      params: { name: displayName, sku: item.sku },
      sessionToken,
      fallback: `Add ${displayName} to my basket`,
      toolCall: {
        tool: "commerce_add_to_cart",
        args: { sku: cartSku },
      },
    });
  };

  return (
    <li className={itemClass}>
      <div className="pick-item--inner">
        <PickBadges badges={badges} />

        <div className="pick-product">
          <div className="product-img">
            <img
              src={item.image ? normalizeProductImage(item.image) : fallbackProductImage()}
              alt={displayName}
              loading="lazy"
              draggable={false}
              onError={handleProductImageError}
            />
          </div>
          <div className="product-inner">
            <div className="product-sku">
              <button
                type="button"
                onClick={handleSkuCopy}
                title={`Copy SKU: ${item.sku}`}
              >
                {item.sku}
              </button>
            </div>
            <div className="product-ufn">
              <ProductNameText name={item.name} sku={item.sku} />
            </div>
            {typeof item.rating === "number" && item.rating > 0 && (
              <div className="product-rating">
                <RatingStars
                  rating={item.rating}
                  className="product-rating__stars"
                  size={10}
                />
                <span className="product-rating__txt">
                  {item.rating.toFixed(1)}
                  {typeof item.reviewCount === "number" && item.reviewCount > 0
                    ? ` (${item.reviewCount.toLocaleString()})`
                    : ""}
                </span>
              </div>
            )}
            <PickProductPrice price={item.price} />
            {availabilityLabel ? (
              <div className="product-availability">{availabilityLabel}</div>
            ) : null}
          </div>
        </div>

        <div className="pick-btns">
          <Button
            variant="secondary"
            size="xs"
            onClick={() => handleLearnMore()}
          >
            {learnMoreLabel}
          </Button>
          {canPurchase ? (
            <Button
              variant="primary"
              size="xs"
              onClick={() => handleAddToBasket()}
            >
              {addToBasketLabel}
            </Button>
          ) : null}
        </div>

        {keyFeatures.length > 0 && (
          <div className="pick-etc--info">
            <ul className="info-list">
              {keyFeatures.slice(0, _MAX_FEATURES).map((f) => (
                <li key={f} className="info-item">
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </li>
  );
}

export default PickProductCard;
